/**
 * All persistent state for the MAHI conversation flow — phase (orb / panel /
 * chip), message history, and the streaming turn in flight. Kept out of
 * DiscoveryFeed and out of MahiChatPanel itself because message history has
 * to survive the panel unmounting (tapping the chip "reopens conversation
 * with full history for refining"), while the phase has to be readable from
 * three different DOM locations at once — the bottom-row orb, the fixed
 * overlay panel, and the results chip above the card stack — that all share
 * one `layoutId` so framer-motion can FLIP between whichever of them is
 * currently mounted.
 *
 * `transport` is swappable by design: stop point (a) runs on
 * `createMockTransport()` from mahiMockTransport.js; wiring the real agent
 * at stop point (b) means passing a transport that speaks the same
 * `{sendTurn({message, onEvent}) => cancel}` shape over the live SSE
 * endpoint — nothing here or in MahiChatPanel needs to change.
 */
import { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import mahi from '../../api';
import deriveChipLabel from './chipLabel';

const HANDOFF_HOLD_MS = 900; // "hold for a beat to be read" — spec step 7

export default function useMahiConversation({ user, transport, onHandoff, onClear }) {
  const [phase, setPhase] = useState('orb'); // 'orb' | 'panel' | 'chip'
  const [query, setQuery] = useState(null); // { label, caption } | null
  const [messages, setMessages] = useState([]);
  const [announcement, setAnnouncement] = useState('');
  // Which category chip is mid-fetch, so it can show its own spinner rather
  // than the panel going quiet for a moment with nothing to explain it.
  const [browsing, setBrowsing] = useState(null);

  const idRef = useRef(0);
  const nextId = () => `mahi-msg-${idRef.current++}`;
  const firstUserMessageRef = useRef('');
  const cancelRef = useRef(null);
  // The server-side thread this deck came out of. Read off the `done` frame
  // rather than asked of the transport, because it is what the deck needs it
  // for: `/inventory/more` reads the search's filters back off the
  // conversation's own turns, so continuing a deck means naming the thread
  // that produced it. Null for a category tap, which has no thread.
  const conversationIdRef = useRef(null);

  const openPanel = useCallback(() => {
    setPhase('panel');
    setMessages((ms) => {
      if (ms.length > 0) return ms;
      const firstName = (user?.name || '').trim().split(' ')[0];
      const greeting = firstName
        ? `Hey ${firstName} - what are we hunting for today?`
        : "Hey - what are we hunting for today?";
      return [{ id: nextId(), role: 'mahi', text: greeting, streaming: false, isIntro: false }];
    });
  }, [user?.name]);

  const closePanel = useCallback(() => {
    // Conversation-only path: closing without results goes back to the orb.
    // Closing a panel reopened from a chip (or one that already produced
    // results this session) goes back to the chip, not the orb.
    setPhase((p) => (p === 'panel' ? (query ? 'chip' : 'orb') : p));
  }, [query]);

  const reopenFromChip = useCallback(() => {
    setPhase('panel');
  }, []);

  const clearQuery = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = null;
    setPhase('orb');
    setQuery(null);
    setMessages([]);
    firstUserMessageRef.current = '';
    conversationIdRef.current = null;
    // A cleared search starts a genuinely new conversation server-side too —
    // otherwise refining an old thread and starting a fresh one would share
    // context the user has no way of knowing about.
    transport.reset?.();
    onClear?.();
  }, [onClear, transport]);

  const sendMessage = useCallback((rawText) => {
    const text = rawText.trim();
    if (!text) return;
    if (!firstUserMessageRef.current) firstUserMessageRef.current = text;

    const userMsgId = nextId();
    const mahiMsgId = nextId();
    setMessages((ms) => ([
      ...ms,
      { id: userMsgId, role: 'user', text, streaming: false, isIntro: false },
      { id: mahiMsgId, role: 'mahi', text: '', streaming: true, isIntro: false },
    ]));

    cancelRef.current = transport.sendTurn({
      message: text,
      onEvent: (ev) => {
        if (ev.type === 'text') {
          setMessages((ms) => ms.map((m) => (
            m.id === mahiMsgId ? { ...m, text: m.text + ev.text } : m
          )));
          return;
        }
        if (ev.type === 'error') {
          const warm = "Let me think on that - try me again in a sec.";
          setMessages((ms) => ms.map((m) => (
            m.id === mahiMsgId ? { ...m, text: warm, streaming: false } : m
          )));
          setAnnouncement(warm);
          return;
        }
        if (ev.type === 'done') {
          if (ev.conversation_id) conversationIdRef.current = ev.conversation_id;
          const hasProducts = Boolean(ev.products?.length);
          setMessages((ms) => ms.map((m) => (
            m.id === mahiMsgId ? { ...m, text: ev.text, streaming: false, isIntro: hasProducts } : m
          )));
          setAnnouncement(ev.text);
          if (hasProducts) {
            setTimeout(() => {
              const nextQuery = { label: deriveChipLabel(firstUserMessageRef.current), caption: ev.text };
              setQuery(nextQuery);
              setPhase('chip');
              setAnnouncement(`Loaded ${ev.products.length} picks for you`);
              onHandoff?.(ev.products, {
                ...nextQuery,
                mahiMessageId: mahiMsgId,
                // How this deck keeps going once these run out — see
                // useDeckContinuation. Omitted when the turn produced no
                // conversation id, which leaves the deck a fixed list rather
                // than continuing against the wrong thread.
                source: conversationIdRef.current
                  ? { conversationId: conversationIdRef.current }
                  : null,
              });
            }, HANDOFF_HOLD_MS);
          }
        }
      },
    });
  }, [transport, onHandoff]);

  /**
   * Tapping a category. Straight to products, no LLM call.
   *
   * Deliberately not routed through `sendMessage`. The agent's first turn
   * offers no tools by design (see backend conversation.stage_for), so a
   * category sent as a chat message comes back as "what's the occasion?" —
   * the exact friction the tap exists to skip. This hits /inventory/browse
   * instead: no LLM, no stage machine, ~100ms.
   *
   * Everything after the fetch is the same handoff a real search performs, so
   * the results chip, the cascade and the deck behave identically however the
   * products were found.
   *
   * The backend still opens a `conversations` thread for the tap (see
   * inventory.py::browse) and hands its id back as `conversation_id` — seeded
   * into the transport and remembered here so that if the shopper reopens
   * chat from this same results chip and types a refinement ("in black under
   * 10000"), that message continues this thread instead of starting a brand
   * new one that never heard the word "skirts". This is what fixes MAHI
   * losing the category between the chip and the chat.
   */
  const browseCategory = useCallback(async ({ key, label }) => {
    cancelRef.current?.();
    cancelRef.current = null;
    setBrowsing(key);
    try {
      const { data } = await mahi.get(
        `/inventory/browse?${new URLSearchParams({ categories: key, limit: '30', label })}`);
      const products = data.items || [];
      if (!products.length) {
        toast(`Nothing in ${label.toLowerCase()} right now`);
        return;
      }
      const nextQuery = { label, caption: '' };
      const conversationId = data.conversation_id || null;
      // Named after the chip rather than left blank, so a later refinement
      // that produces new results still derives a chip label of "Skirts"
      // instead of the generic "Your search" fallback.
      firstUserMessageRef.current = conversationId ? label : '';
      conversationIdRef.current = conversationId;
      transport.seedConversationId?.(conversationId);
      setMessages([]);
      setQuery(nextQuery);
      setPhase('chip');
      setAnnouncement(`Loaded ${products.length} ${label.toLowerCase()}`);
      // Prefer the thread the tap just opened, same as every other handoff —
      // /inventory/more reads its filters straight off that conversation's
      // turns. Only falls back to the raw category when, for some reason,
      // the backend didn't hand one back.
      onHandoff?.(products, {
        ...nextQuery,
        source: conversationId ? { conversationId } : { categories: [key] },
      });
    } catch {
      toast.error('Could not load that just now');
    } finally {
      setBrowsing(null);
    }
  }, [onHandoff, transport]);

  // Chat history (Phase 2), "Show me these again" — replays the full
  // handoff (chip + cascade) for a past search, "exactly as if she'd just
  // returned them" rather than an instant, silent swap.
  const restoreFromHistory = useCallback(({ conversationId, products, label, caption }) => {
    cancelRef.current?.();
    cancelRef.current = null;
    setMessages([]);
    firstUserMessageRef.current = '';
    conversationIdRef.current = conversationId || null;
    setQuery({ label, caption });
    setPhase('chip');
    setAnnouncement(`Loaded ${products.length} picks for you`);
    // A replayed search is still a search, so it continues the same way a
    // fresh one does rather than dead-ending at whatever that turn returned.
    onHandoff?.(products, {
      label, caption,
      source: conversationId ? { conversationId } : null,
    });
  }, [onHandoff]);

  // Chat history, "Pick this back up" — reopens the panel with that
  // conversation's own history loaded, and seeds the transport so the next
  // real message continues that same server-side thread rather than
  // starting a fresh one under the same messages.
  const resumeFromHistory = useCallback(({ conversationId, messages: pastMessages, label, caption }) => {
    cancelRef.current?.();
    cancelRef.current = null;
    transport.seedConversationId?.(conversationId);
    conversationIdRef.current = conversationId || null;
    firstUserMessageRef.current = pastMessages.find((m) => m.role === 'user')?.text || '';
    setMessages(pastMessages.map((m) => ({
      id: nextId(),
      role: m.role === 'user' ? 'user' : 'mahi',
      text: m.text,
      streaming: false,
      isIntro: false,
    })));
    setQuery(label ? { label, caption } : null);
    setPhase('panel');
  }, [transport]);

  return {
    phase, query, messages, announcement, browsing,
    openPanel, closePanel, reopenFromChip, clearQuery, sendMessage, browseCategory,
    restoreFromHistory, resumeFromHistory,
  };
}
